import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { EditableImage } from './EditableImage';
import { ImagePlus, Settings, GripVertical, Info } from 'lucide-react';
import { useAdmin } from '@/hooks/useAdmin';
import { useToast } from '@/hooks/use-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface GalleryEditorDialogProps {
  images: string[];
  imageKey?: string;
  category?: string;
  onImagesUpdate?: (urls: string[]) => Promise<void>;
}

// Composant pour une image draggable
interface SortableImageProps {
  id: string;
  image: string;
  index: number;
  imageKey?: string;
  category: string;
  onUpdate: (newUrl: string) => Promise<void>;
  onDelete: () => Promise<void>;
}

const SortableImage = ({ id, image, index, imageKey, category, onUpdate, onDelete }: SortableImageProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <div className="absolute top-2 left-2 z-20 cursor-grab active:cursor-grabbing bg-background/80 rounded p-1" {...attributes} {...listeners}>
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="absolute top-2 right-2 z-20 bg-background/80 rounded px-2 py-1 text-xs font-medium">
        #{index + 1}
      </div>
      <EditableImage
        src={image}
        alt={`Image ${index + 1}`}
        className="w-full aspect-square object-cover rounded-lg border-2 border-border hover:border-primary transition-all"
        bucket="site-assets"
        path={imageKey ? `${category}/${imageKey}/${index}.jpg` : undefined}
        allowDelete={true}
        onImageUpdate={onUpdate}
        onImageDelete={onDelete}
      />
    </div>
  );
};

export const GalleryEditorDialog = ({
  images,
  imageKey,
  category = 'general',
  onImagesUpdate
}: GalleryEditorDialogProps) => {
  const { isAdmin } = useAdmin();
  const { toast } = useToast();
  const [currentImages, setCurrentImages] = useState<string[]>(images || []);
  const [open, setOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sync with parent when images change
  useEffect(() => {
    setCurrentImages(images || []);
  }, [images]);

  if (!isAdmin) return null;

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = currentImages.findIndex((_, i) => `image-${i}` === active.id);
      const newIndex = currentImages.findIndex((_, i) => `image-${i}` === over.id);

      const newImages = arrayMove(currentImages, oldIndex, newIndex);
      setCurrentImages(newImages);

      if (onImagesUpdate) {
        await onImagesUpdate(newImages);
        toast({
          title: "Ordre modifié",
          description: "Les photos ont été réorganisées",
        });
      }
    }
  };

  const handleImageUpdate = async (index: number, newUrl: string) => {
    const newImages = [...currentImages];
    newImages[index] = newUrl;
    setCurrentImages(newImages);
    
    if (onImagesUpdate) {
      await onImagesUpdate(newImages);
    }
  };

  const handleImageDelete = async (index: number) => {
    const newImages = currentImages.filter((_, i) => i !== index);
    setCurrentImages(newImages);
    
    if (onImagesUpdate) {
      await onImagesUpdate(newImages);
      toast({
        title: "Photo supprimée",
        description: `${newImages.length} photo${newImages.length > 1 ? 's' : ''} restante${newImages.length > 1 ? 's' : ''}`,
      });
    }
  };

  const handleAddImage = async (newUrl: string) => {
    const newImages = [...currentImages, newUrl];
    setCurrentImages(newImages);
    
    if (onImagesUpdate) {
      await onImagesUpdate(newImages);
      toast({
        title: "Photo ajoutée",
        description: `${newImages.length} photo${newImages.length > 1 ? 's' : ''} dans la galerie`,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="default"
          size="sm"
          className="absolute top-4 right-4 z-10 shadow-lg gap-2"
        >
          <Settings className="w-4 h-4" />
          Gérer la galerie
          {currentImages.length > 0 && (
            <span className="bg-background text-foreground px-2 py-0.5 rounded-full text-xs font-bold">
              {currentImages.length}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Gestion de la galerie
            {currentImages.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                ({currentImages.length} photo{currentImages.length > 1 ? 's' : ''})
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="flex items-start gap-2 pt-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
            <span className="text-sm">
              Glissez-déposez les photos pour les réorganiser. Cliquez sur une photo pour la remplacer ou la supprimer.
            </span>
          </DialogDescription>
        </DialogHeader>
        
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={currentImages.map((_, index) => `image-${index}`)}
            strategy={rectSortingStrategy}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
              {currentImages.map((img, index) => (
                <SortableImage
                  key={`image-${index}`}
                  id={`image-${index}`}
                  image={img}
                  index={index}
                  imageKey={imageKey}
                  category={category}
                  onUpdate={(newUrl) => handleImageUpdate(index, newUrl)}
                  onDelete={() => handleImageDelete(index)}
                />
              ))}
              
              {/* Bouton pour ajouter une nouvelle image */}
              <div className="relative aspect-square border-2 border-dashed border-border rounded-lg hover:border-primary hover:bg-accent/50 transition-all group cursor-pointer">
                <EditableImage
                  src=""
                  alt="Ajouter une image"
                  className="w-full aspect-square object-cover rounded-lg"
                  bucket="site-assets"
                  path={imageKey ? `${category}/${imageKey}/${currentImages.length}.jpg` : undefined}
                  allowDelete={false}
                  onImageUpdate={handleAddImage}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none gap-2">
                  <ImagePlus className="w-10 h-10 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-sm font-medium text-muted-foreground group-hover:text-primary transition-colors">
                    Ajouter une photo
                  </span>
                </div>
              </div>
            </div>
          </SortableContext>
        </DndContext>

        {currentImages.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <ImagePlus className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">Aucune photo dans la galerie</p>
            <p className="text-sm">Cliquez sur "Ajouter une photo" pour commencer</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
